<template>
    <div class="byb-wrapper w-100">

        <!-- BANNER -->
        <div class="byb-banner" v-if="step === 'banner'" @click="startBuilder">

            <!-- Header: título + precio -->
            <div class="banner-header">
                <div class="banner-header-left">
                    <p class="banner-eyebrow">Elige tus 3 proteínas</p>
                    <h2 class="banner-title">Arma tu <em>Box</em></h2>
                    <p class="banner-sub">Escoge lo que va en cada tercio — tú decides la combinación</p>
                </div>
                <div class="banner-header-right">
                    <div class="banner-price-label">Precio</div>
                    <div class="banner-price">${{ BOX_INFO.price }}</div>
                    <div class="banner-includes">{{ BOX_INFO.includes }}</div>
                </div>
            </div>

            <!-- Grid de productos -->
            <div class="banner-products-grid">
                <div
                    v-for="(pk, idx) in productKeys"
                    :key="pk"
                    class="banner-prod-col"
                >
                    <span class="banner-prod-tercio">Tercio {{ idx + 1 }}</span>
                    <div class="banner-prod-img-wrap">
                        <img :src="products[pk].image" class="banner-prod-img" :alt="products[pk].name">
                    </div>
                    <div class="banner-prod-name">{{ products[pk].name }}</div>
                    <div class="banner-prod-qty">{{ products[pk].slotQty }} {{ products[pk].unitLabelPlural }}</div>
                </div>
            </div>

            <!-- Footer CTA -->
            <div class="banner-footer">
                <p class="banner-footer-hint">Elige <strong>una combinación diferente</strong> en cada tercio</p>
                <button class="btn-configure">Arma tu Box →</button>
            </div>

        </div>

        <!-- BUILDER: acordeón con los 3 tercios siempre visibles -->
        <div class="byb-builder-v2" v-if="step === 'builder'">

            <!-- Header precio -->
            <div class="bdr-header">
                <div>
                    <h3 class="bdr-title">Arma tu Box</h3>
                    <p class="bdr-sub">Elige el pollo de cada tercio</p>
                </div>
                <span class="bdr-price">${{ BOX_INFO.price }}</span>
            </div>

            <!-- Barra de progreso global -->
            <div class="gp-wrap">
                <div class="gp-bar-bg">
                    <div class="gp-bar-fill" :style="{ width: progressPct + '%' }"></div>
                </div>
                <div class="gp-labels">
                    <span
                        v-for="(lbl, i) in ['Primero', 'Segundo', 'Tercero']"
                        :key="i"
                        class="gp-label"
                        :class="{ done: confirmed[i], active: openSlot === i && !confirmed[i] }"
                    >{{ lbl }}</span>
                </div>
            </div>

            <!-- Los 3 tercios -->
            <div class="tercio-list">
                <div
                    v-for="i in [0, 1, 2]"
                    :key="i"
                    class="tercio-block"
                    :class="tercioClass(i)"
                >
                    <!-- Header clicable del tercio -->
                    <div class="tercio-header" @click="toggleTercio(i)">
                        <div class="tercio-num">{{ confirmed[i] ? '✓' : i + 1 }}</div>

                        <div class="tercio-header-info">
                            <!-- Vista "confirmado": preview con miniatura -->
                            <template v-if="confirmed[i] && slots[i]">
                                <div class="tercio-done-preview">
                                    <img :src="products[slots[i]].image" class="tdp-img" :alt="products[slots[i]].name">
                                    <span class="tdp-name">{{ products[slots[i]].shortName }}</span>
                                    <span class="tdp-variant" :class="slotVariants[i]">
                                        {{ slotVariants[i] === 'spicy' ? '🌶️ Spicy' : 'Normal' }}
                                    </span>
                                </div>
                                <span class="tercio-done-hint">Toca para cambiar</span>
                            </template>
                            <!-- Vista vacío / activo -->
                            <template v-else>
                                <div class="tercio-title">{{ STEP_TITLES[i] }}</div>
                                <div class="tercio-subtitle">{{ STEP_SUBS[i] }}</div>
                            </template>
                        </div>

                        <span class="tercio-chevron" :class="{ open: openSlot === i }">▼</span>
                    </div>

                    <!-- Drawer con el selector -->
                    <transition name="drawer">
                        <div v-if="openSlot === i" class="tercio-drawer">
                            <div class="drawer-inner">

                                <!-- Grid de productos -->
                                <div class="prod-grid">
                                    <div
                                        v-for="pk in productKeys"
                                        :key="pk"
                                        class="prod-card"
                                        :class="{ selected: currentSelectionForSlot(i) === pk }"
                                        @click="selectProdInSlot(i, pk)"
                                    >
                                        <div class="prod-img-wrap">
                                            <img :src="products[pk].image" class="prod-img" :alt="products[pk].name">
                                        </div>
                                        <div class="prod-card-name">{{ products[pk].shortName }}</div>
                                        <div class="prod-card-qty">{{ products[pk].slotQty }} {{ products[pk].unitLabelPlural }}</div>
                                        <div class="prod-sel-dot" :class="{ on: currentSelectionForSlot(i) === pk }">✓</div>
                                    </div>
                                </div>

                                <!-- Toggle Normal / Spicy -->
                                <transition name="fade-slide">
                                    <div class="spicy-toggle" v-if="canConfirmSlot(i)">
                                        <span class="spicy-toggle-label">¿Normal o con su toque?</span>
                                        <div class="spicy-toggle-btns">
                                            <button
                                                class="spicy-btn"
                                                :class="{ 'spicy-btn-active': slotVariants[i] === 'normal' }"
                                                @click="setSlotVariant(i, 'normal')"
                                            >Normal</button>
                                            <button
                                                class="spicy-btn spicy-btn-hot"
                                                :class="{ 'spicy-btn-active-hot': slotVariants[i] === 'spicy' }"
                                                @click="setSlotVariant(i, 'spicy')"
                                            >🌶️ Spicy</button>
                                        </div>
                                    </div>
                                </transition>

                                <button class="btn-confirm-slot" :disabled="!canConfirmSlot(i)" @click="confirmSlot(i)">
                                    ¡Ese es el bueno!
                                </button>
                                <button class="btn-cancel-slot" @click="cancelSlot(i)">Mejor no</button>

                            </div>
                        </div>
                    </transition>

                </div>
            </div><!-- /tercio-list -->

            <!-- Sidebar desktop: resumen visual de la caja -->
            <div class="byb-sidebar">
                <div class="sidebar-box-card">
                    <div class="sidebar-box-header">
                        <span class="sidebar-box-title">Tu Box</span>
                        <span class="sidebar-box-price">${{ BOX_INFO.price }}</span>
                    </div>
                    <div class="sidebar-slots">
                        <div
                            v-for="i in [0, 1, 2]"
                            :key="i"
                            class="sidebar-slot"
                            :class="{ filled: confirmed[i], active: !confirmed[i] && openSlot === i }"
                        >
                            <template v-if="confirmed[i] && slots[i]">
                                <img :src="products[slots[i]].image" class="sidebar-slot-img" :alt="products[slots[i]].name">
                                <div class="sidebar-slot-info">
                                    <span class="sidebar-slot-name">{{ products[slots[i]].shortName }}</span>
                                    <span class="sidebar-slot-qty">{{ products[slots[i]].slotQty }} {{ products[slots[i]].unitLabelPlural }}</span>
                                </div>
                                <span class="sidebar-slot-variant" :class="slotVariants[i]">
                                    {{ slotVariants[i] === 'spicy' ? '🌶️ Spicy' : 'Normal' }}
                                </span>
                            </template>
                            <template v-else>
                                <div class="sidebar-slot-empty-num">{{ i + 1 }}</div>
                                <span class="sidebar-slot-empty-lbl">{{ i === openSlot ? 'Eligiendo...' : 'Pendiente' }}</span>
                            </template>
                        </div>
                    </div>
                    <div class="sidebar-includes">
                        <strong>Incluye:</strong> {{ BOX_INFO.includes }}
                    </div>
                </div>
            </div>

            <!-- Botón de carrito (siempre visible, desactivado hasta completar) -->
            <div class="cart-btn-wrap">
                <button class="btn-cart-main" :disabled="!allConfirmed" @click="goToSummary">
                    <span class="cart-btn-label">{{ allConfirmed ? '¡Listo! Ver mi caja →' : '🛒 ¡Me lo llevo!' }}</span>
                    <span class="cart-btn-price">${{ BOX_INFO.price }}</span>
                </button>
                <button class="btn-reset-link" @click="resetAll">Empezar de cero</button>
            </div>

        </div><!-- /byb-builder-v2 -->

        <!-- RESUMEN -->
        <div class="byb-summary" v-if="step === 'summary'">
            <h3 class="step-title">¡Así quedó tu caja!</h3>
            <p class="step-sub">Todo listo. Se ve increíble</p>

            <div class="summary-size-row d-flex justify-content-between align-items-center mb-3">
                <span class="summary-size-label">Caja Familiar</span>
                <span class="summary-price">${{ BOX_INFO.price }}</span>
            </div>

            <div class="summary-list">
                <div class="summary-row d-flex align-items-center gap-2" v-for="(slot, i) in slots" :key="i">
                    <img :src="products[slot].image" class="summary-thumb">
                    <div class="flex-fill">
                        <div class="summary-row-top">
                            <span class="summary-tercio-badge">Favorito {{ i + 1 }}</span>
                            <span class="summary-product">{{ products[slot].name }}</span>
                            <span v-if="slotVariants[i] === 'spicy'" class="summary-spicy">🌶️ Spicy</span>
                        </div>
                        <span class="summary-qty">{{ products[slot].slotQty }} {{ products[slot].unitLabelPlural }}</span>
                    </div>
                </div>
            </div>

            <div class="summary-includes">
                <span class="includes-label">Incluye:</span> {{ BOX_INFO.includes }}
            </div>

            <button class="btn-cart">🛒 ¡Me lo llevo!</button>
            <button class="btn-back mt-2" @click="goBack">← Le quiero cambiar algo</button>
        </div>

    </div>
</template>

<script>
const PRODUCTS = {
    big_mary: {
        name: "Big Mary's",
        shortName: "Big Mary's",
        unitLabel: 'sándwich',
        unitLabelPlural: 'sándwiches',
        slotQty: 3,
        image: require('@/assets/images/products/mx/big-mary-single.png'),
        boxImage: require('@/assets/images/box/big-mary.png'),
    },
    tenders: {
        name: 'Tenders',
        shortName: 'Tenders',
        unitLabel: 'tender',
        unitLabelPlural: 'tenders',
        slotQty: 15,
        image: require('@/assets/images/products/mx/pop-ins-masked.png'),
        boxImage: require('@/assets/images/box/tenders.png'),
    },
    signature: {
        name: 'Signature Chicken',
        shortName: 'Piezas',
        unitLabel: 'pieza',
        unitLabelPlural: 'piezas',
        slotQty: 8,
        image: require('@/assets/images/products/signature-chicken.png'),
        boxImage: require('@/assets/images/box/bone-chicken.png'),
    },
};

const BOX_INFO = {
    price: 799,
    includes: 'Puré Grande y Ensalada Grande',
};

export default {
    name: 'BuildYourBox',
    data() {
        return {
            step: 'banner',         // 'banner' | 'builder' | 'summary'
            openSlot: 0,            // índice del drawer abierto (-1 = ninguno)
            slots: [null, null, null],
            slotVariants: ['normal', 'normal', 'normal'],
            confirmed: [false, false, false],
            pendingSel: [null, null, null],
            STEP_TITLES: [
                'Elige tu primer favorito',
                '¿Y cuál va en segundo? Tú mandas',
                '¡Ya mero! El último',
            ],
            STEP_SUBS: [
                'El que más se te antoje',
                'Uno más y casi la tienes',
                'Ciérrala con lo que más te llama',
            ],
            products: PRODUCTS,
            productKeys: Object.keys(PRODUCTS),
            BOX_INFO,
            bannerImg: require('@/assets/images/products/mx/combos-category.png'),
        };
    },
    computed: {
        allConfirmed() {
            return this.confirmed.every(Boolean);
        },
        progressPct() {
            return (this.confirmed.filter(Boolean).length / 3) * 100;
        },
    },
    methods: {
        startBuilder() {
            this.step = 'builder';
            this.openSlot = 0;
        },
        toggleTercio(i) {
            if (this.openSlot === i) {
                this.openSlot = -1;
            } else {
                this.openSlot = i;
                const updated = [...this.pendingSel];
                updated[i] = null;
                this.pendingSel = updated;
            }
        },
        currentSelectionForSlot(i) {
            return this.pendingSel[i] !== null ? this.pendingSel[i] : this.slots[i];
        },
        canConfirmSlot(i) {
            return this.pendingSel[i] !== null || this.slots[i] !== null;
        },
        selectProdInSlot(slot, key) {
            const updated = [...this.pendingSel];
            updated[slot] = key;
            this.pendingSel = updated;
        },
        setSlotVariant(slot, v) {
            const updated = [...this.slotVariants];
            updated[slot] = v;
            this.slotVariants = updated;
        },
        confirmSlot(slot) {
            const newSlots = [...this.slots];
            newSlots[slot] = this.pendingSel[slot] || this.slots[slot];
            this.slots = newSlots;

            const newConfirmed = [...this.confirmed];
            newConfirmed[slot] = true;
            this.confirmed = newConfirmed;

            const newPending = [...this.pendingSel];
            newPending[slot] = null;
            this.pendingSel = newPending;

            // Auto-abrir el siguiente tercio sin confirmar
            const nextEmpty = [0, 1, 2].find(i => !newConfirmed[i]);
            this.openSlot = nextEmpty !== undefined ? nextEmpty : -1;
        },
        cancelSlot(slot) {
            const updated = [...this.pendingSel];
            updated[slot] = null;
            this.pendingSel = updated;
            this.openSlot = -1;
        },
        goToSummary() {
            this.step = 'summary';
        },
        goBack() {
            this.step = 'builder';
        },
        resetAll() {
            this.step = 'banner';
            this.openSlot = 0;
            this.slots = [null, null, null];
            this.slotVariants = ['normal', 'normal', 'normal'];
            this.confirmed = [false, false, false];
            this.pendingSel = [null, null, null];
        },
        tercioClass(i) {
            if (this.confirmed[i]) return 'is-done';
            if (this.openSlot === i) return 'is-active';
            return 'is-empty';
        },
    },
};
</script>

<style scoped>
/* ========== WRAPPER ========== */
.byb-wrapper {
    max-width: 1200px;
    margin: 0 auto;
    padding: 16px;
}

/* ========== BANNER ========== */
.byb-banner {
    background: #fff;
    border-radius: 20px;
    border: 2px solid var(--nonagenary);
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.byb-banner:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
}

/* Header */
.banner-header {
    background: linear-gradient(135deg, var(--octagenary), #efe6da);
    padding: 22px 26px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    border-bottom: 2px solid var(--nonagenary);
}
.banner-eyebrow {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--primary);
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 6px;
}
.banner-eyebrow::before {
    content: '';
    display: inline-block;
    width: 18px;
    height: 2px;
    background: var(--primary);
    border-radius: 1px;
}
.banner-title {
    font-size: 40px;
    font-weight: 900;
    line-height: 0.92;
    color: var(--text3);
    text-transform: uppercase;
    letter-spacing: -0.01em;
    margin: 0 0 8px;
}
.banner-title em { color: var(--primary); font-style: normal; }
.banner-sub {
    font-size: 13px;
    color: var(--text11);
}
.banner-header-right { text-align: right; flex-shrink: 0; }
.banner-price-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text11);
}
.banner-price {
    font-size: 42px;
    font-weight: 900;
    color: var(--primary);
    line-height: 1;
    margin: 2px 0 4px;
}
.banner-includes { font-size: 11px; color: var(--text11); }

/* Grid de productos */
.banner-products-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    background: #fff;
}
.banner-prod-col {
    padding: 24px 16px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    border-right: 1.5px solid var(--nonagenary);
    background: #fff;
    position: relative;
    transition: background 0.18s;
}
.banner-prod-col:last-child { border-right: none; }
.banner-prod-col:hover { background: #fffaf5; }
.banner-prod-tercio {
    position: absolute;
    top: 10px;
    left: 12px;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--text15);
}
.banner-prod-img-wrap {
    width: 150px;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
}
.banner-prod-img {
    max-width: 150px;
    max-height: 120px;
    object-fit: contain;
    filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.12));
    transition: transform 0.2s;
}
.banner-prod-col:hover .banner-prod-img { transform: scale(1.05) translateY(-3px); }
.banner-prod-name {
    font-size: 15px;
    font-weight: 800;
    color: var(--text3);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 3px;
}
.banner-prod-qty { font-size: 12px; color: var(--text11); }

/* Footer CTA */
.banner-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 20px;
    background: var(--primary);
    border-top: 2px solid var(--nonagenary);
}
.banner-footer-hint {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.75);
}
.banner-footer-hint strong { color: #fff; font-weight: 700; }
.btn-configure {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 13px 22px;
    border: none;
    border-radius: 12px;
    background: #1a1a1a;
    color: #fff;
    font-size: 16px;
    font-weight: 800;
    cursor: pointer;
    transition: background 0.2s;
    letter-spacing: 0.04em;
    white-space: nowrap;
    flex-shrink: 0;
}
.btn-configure:hover { background: #333; }

/* ========== BUILDER V2 ========== */
.byb-builder-v2 {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    animation: builderOpen 0.3s ease;
}
@media (min-width: 768px) {
    .byb-builder-v2 {
        grid-template-columns: 1fr 340px;
        grid-template-areas:
            "header  sidebar"
            "progress sidebar"
            "tercios  sidebar"
            "cart     sidebar";
        align-items: start;
    }
    .bdr-header   { grid-area: header; }
    .gp-wrap      { grid-area: progress; }
    .tercio-list  { grid-area: tercios; }
    .cart-btn-wrap { grid-area: cart; }
    .byb-sidebar  { grid-area: sidebar; position: sticky; top: 16px; }
}
@keyframes builderOpen {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
}

/* Header precio */
.bdr-header {
    background: #fff;
    border-radius: 16px;
    border: 2px solid var(--nonagenary);
    padding: 14px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.bdr-title {
    font-size: 17px;
    font-weight: 800;
    color: var(--text3);
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.bdr-sub {
    font-size: 12px;
    color: var(--text11);
    margin: 2px 0 0;
}
.bdr-price {
    font-size: 26px;
    font-weight: 800;
    color: var(--primary);
}

/* Barra de progreso global */
.gp-wrap {
    background: #fff;
    border-radius: 14px;
    border: 2px solid var(--nonagenary);
    padding: 12px 16px;
}
.gp-bar-bg {
    height: 6px;
    background: var(--nonagenary);
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 8px;
}
.gp-bar-fill {
    height: 100%;
    background: var(--primary);
    border-radius: 3px;
    transition: width 0.4s ease;
}
.gp-labels {
    display: flex;
    justify-content: space-between;
}
.gp-label {
    font-size: 11px;
    font-weight: 600;
    color: var(--text11);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    transition: color 0.3s;
}
.gp-label.done   { color: var(--primary); }
.gp-label.active { color: var(--text3); font-weight: 800; }

/* ========== TERCIOS ========== */
.tercio-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.tercio-block {
    background: #fff;
    border: 2px solid var(--nonagenary);
    border-radius: 18px;
    overflow: hidden;
    transition: border-color 0.2s, box-shadow 0.2s;
}
.tercio-block.is-active {
    border-color: var(--primary);
    box-shadow: 0 3px 16px rgba(232, 118, 43, 0.1);
}
.tercio-block.is-done {
    border-color: var(--primary);
}

/* Header del tercio */
.tercio-header {
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    user-select: none;
    transition: background 0.15s;
}
.tercio-header:hover { background: rgba(0, 0, 0, 0.02); }

.tercio-num {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 800;
    flex-shrink: 0;
    transition: all 0.25s;
}
.tercio-block.is-empty .tercio-num  { background: var(--octagenary); color: var(--text15); }
.tercio-block.is-active .tercio-num { background: var(--primary); color: #fff; box-shadow: 0 0 0 4px rgba(232, 118, 43, 0.2); }
.tercio-block.is-done .tercio-num   { background: var(--primary); color: #fff; }

.tercio-header-info { flex: 1; min-width: 0; }
.tercio-title {
    font-size: 15px;
    font-weight: 800;
    color: var(--text3);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.tercio-block.is-empty .tercio-title { color: var(--text15); }
.tercio-subtitle { font-size: 12px; color: var(--text11); margin-top: 2px; }
.tercio-block.is-empty .tercio-subtitle { color: var(--text15); }

/* Preview del tercio confirmado */
.tercio-done-preview {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.tdp-img {
    width: 38px;
    height: 30px;
    object-fit: contain;
    filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.12));
    flex-shrink: 0;
}
.tdp-name {
    font-size: 14px;
    font-weight: 800;
    color: var(--text3);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.tdp-variant {
    font-size: 10px;
    font-weight: 700;
    border-radius: 20px;
    padding: 2px 8px;
    text-transform: uppercase;
}
.tdp-variant.normal { background: rgba(232, 118, 43, 0.12); color: var(--primary); }
.tdp-variant.spicy  { background: rgba(220, 60, 30, 0.10); color: #dc3c1e; }
.tercio-done-hint { font-size: 11px; color: var(--text11); display: block; margin-top: 2px; }

.tercio-chevron {
    font-size: 13px;
    color: var(--text15);
    transition: transform 0.25s, color 0.2s;
    flex-shrink: 0;
}
.tercio-chevron.open { transform: rotate(180deg); color: var(--primary); }

/* Drawer */
.drawer-enter-active {
    transition: max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease;
    max-height: 600px;
    overflow: hidden;
}
.drawer-leave-active {
    transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease;
    max-height: 600px;
    overflow: hidden;
}
.drawer-enter, .drawer-leave-to {
    max-height: 0 !important;
    opacity: 0;
}

.tercio-drawer { overflow: hidden; }
.drawer-inner {
    padding: 0 14px 16px;
    border-top: 1.5px solid var(--nonagenary);
    margin: 0 2px;
}

/* Grid de productos */
.prod-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-top: 14px;
}
.prod-card {
    background: var(--octagenary);
    border: 2px solid var(--nonagenary);
    border-radius: 14px;
    padding: 12px 8px 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    transition: all 0.18s;
    text-align: center;
}
.prod-card:hover {
    border-color: var(--primary);
    transform: translateY(-2px);
    box-shadow: 0 3px 10px rgba(232, 118, 43, 0.12);
}
.prod-card.selected {
    border-color: var(--primary);
    background: rgba(232, 118, 43, 0.05);
    box-shadow: 0 0 0 3px rgba(232, 118, 43, 0.1);
}
.prod-img-wrap {
    width: 100%;
    height: 90px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
}
.prod-img {
    max-width: 100%;
    max-height: 90px;
    object-fit: contain;
    filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.15));
    transition: transform 0.2s;
}
@media (min-width: 768px) {
    .prod-img-wrap { height: 120px; }
    .prod-img      { max-height: 120px; }
    .prod-card-name { font-size: 14px; }
    .prod-card-qty  { font-size: 11px; }
}
.prod-card:hover .prod-img { transform: scale(1.06); }
.prod-card-name {
    font-size: 13px;
    font-weight: 800;
    color: var(--text3);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    line-height: 1.15;
}
.prod-card-qty { font-size: 10px; color: var(--text11); margin-top: 2px; }
.prod-sel-dot {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid var(--nonagenary);
    margin: 7px auto 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    color: transparent;
    transition: all 0.2s;
}
.prod-sel-dot.on { background: var(--primary); border-color: var(--primary); color: #fff; }

/* ========== SPICY TOGGLE ========== */
.spicy-toggle {
    margin-top: 12px;
    padding: 12px 14px;
    background: var(--octagenary);
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}
.spicy-toggle-label {
    font-size: 12px;
    font-weight: 700;
    color: var(--text9);
    white-space: nowrap;
}
.spicy-toggle-btns { display: flex; gap: 8px; flex: 1; }
.spicy-btn {
    flex: 1;
    padding: 8px 10px;
    border: 2px solid var(--nonagenary);
    border-radius: 25px;
    background: #fff;
    font-size: 13px;
    font-weight: 600;
    color: var(--text9);
    cursor: pointer;
    transition: all 0.2s;
}
.spicy-btn-active {
    border-color: var(--primary);
    background: rgba(232, 118, 43, 0.08);
    color: var(--primary);
}
.spicy-btn-active-hot {
    border-color: #dc3c1e;
    background: rgba(220, 60, 30, 0.08);
    color: #dc3c1e;
}

/* Botones del drawer */
.btn-confirm-slot {
    display: block;
    width: 100%;
    padding: 12px;
    margin-top: 12px;
    border: none;
    border-radius: 12px;
    background: var(--primary);
    color: #fff;
    font-size: 15px;
    font-weight: 800;
    cursor: pointer;
    transition: background 0.2s, transform 0.1s;
    letter-spacing: 0.04em;
}
.btn-confirm-slot:hover { background: var(--vigequinary); }
.btn-confirm-slot:active { transform: scale(0.98); }
.btn-confirm-slot:disabled { background: var(--nonagenary); color: var(--text11); cursor: not-allowed; }
.btn-cancel-slot {
    display: block;
    width: 100%;
    padding: 8px;
    margin-top: 4px;
    border: none;
    background: transparent;
    color: var(--text11);
    font-size: 12px;
    cursor: pointer;
    text-align: center;
}
.btn-cancel-slot:hover { color: var(--primary); }

/* ========== BOTÓN CARRITO ========== */
.cart-btn-wrap {
    background: #fff;
    border-radius: 18px;
    border: 2px solid var(--nonagenary);
    padding: 14px 16px;
}
.btn-cart-main {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 15px 20px;
    border: none;
    border-radius: 14px;
    background: var(--primary);
    color: #fff;
    font-size: 16px;
    font-weight: 800;
    cursor: pointer;
    transition: background 0.2s, transform 0.1s;
    box-shadow: 0 4px 16px rgba(232, 118, 43, 0.3);
    letter-spacing: 0.04em;
}
.btn-cart-main:hover { background: var(--vigequinary); }
.btn-cart-main:active { transform: scale(0.98); }
.btn-cart-main:disabled {
    background: var(--nonagenary);
    color: var(--text11);
    cursor: not-allowed;
    box-shadow: none;
}
.cart-btn-label { flex: 1; text-align: left; }
.cart-btn-price { font-size: 20px; font-weight: 900; }
.btn-reset-link {
    display: block;
    width: 100%;
    margin-top: 8px;
    background: none;
    border: none;
    color: var(--text15);
    font-size: 12px;
    text-decoration: underline;
    cursor: pointer;
    text-align: center;
    padding: 4px;
}
.btn-reset-link:hover { color: var(--primary); }

/* ========== RESUMEN ========== */
.byb-summary {
    background: #fff;
    border-radius: 20px;
    border: 2px solid var(--nonagenary);
    padding: 22px 18px 24px;
    animation: builderOpen 0.3s ease;
}
.step-title {
    font-size: 20px;
    font-weight: 800;
    color: var(--text3);
    margin: 0 0 4px;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.step-sub {
    font-size: 13px;
    color: var(--text11);
    text-align: center;
    margin: 0 0 18px;
}
.summary-size-row {
    background: var(--octagenary);
    border-radius: 12px;
    padding: 10px 14px;
}
.summary-size-label { font-size: 15px; font-weight: 700; color: var(--text3); }
.summary-price { font-size: 22px; font-weight: 800; color: var(--primary); }
.summary-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
}
.summary-row {
    padding: 10px 12px;
    border: 1.5px solid var(--nonagenary);
    border-radius: 12px;
    background: var(--octagenary);
}
.summary-thumb { width: 44px; height: 34px; object-fit: contain; flex-shrink: 0; }
.summary-row-top { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.summary-tercio-badge {
    font-size: 10px;
    font-weight: 700;
    color: var(--text11);
    background: rgba(255, 255, 255, 0.8);
    border-radius: 20px;
    padding: 2px 8px;
    white-space: nowrap;
}
.summary-product { font-size: 14px; font-weight: 700; color: var(--text3); }
.summary-spicy { font-size: 12px; }
.summary-qty { font-size: 12px; color: var(--text11); display: block; margin-top: 2px; }
.summary-includes {
    font-size: 12px;
    color: var(--text11);
    padding: 10px 0;
    border-top: 1px solid var(--nonagenary);
    margin-bottom: 4px;
}
.includes-label { font-weight: 700; color: var(--text9); margin-right: 4px; }

/* ========== BOTONES GENERALES ========== */
.btn-cart {
    display: block;
    width: 100%;
    padding: 15px;
    border: none;
    border-radius: 14px;
    background: var(--primary);
    color: #fff;
    font-size: 17px;
    font-weight: 800;
    cursor: pointer;
    transition: background 0.2s, transform 0.1s;
    box-shadow: 0 4px 16px rgba(232, 118, 43, 0.3);
    letter-spacing: 0.04em;
}
.btn-cart:hover { background: var(--vigequinary); }
.btn-cart:active { transform: scale(0.97); }
.btn-back {
    display: block;
    width: 100%;
    padding: 10px;
    border: none;
    background: transparent;
    color: var(--text11);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    text-align: center;
}
.btn-back:hover { color: var(--primary); }

/* ========== ANIMACIONES ========== */
.fade-slide-enter-active { transition: all 0.25s ease; }
.fade-slide-leave-active { transition: all 0.2s ease; }
.fade-slide-enter-from, .fade-slide-enter { opacity: 0; transform: translateY(-8px); }
.fade-slide-leave-to   { opacity: 0; transform: translateY(-4px); }

/* ========== SIDEBAR ========== */
.byb-sidebar { display: none; }
@media (min-width: 768px) {
    .byb-sidebar { display: block; }
}
.sidebar-box-card {
    background: #fff;
    border: 2px solid var(--nonagenary);
    border-radius: 18px;
    overflow: hidden;
}
.sidebar-box-header {
    background: linear-gradient(90deg, #1a1a1a, #2c2c2c);
    padding: 14px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.sidebar-box-title {
    font-size: 14px;
    font-weight: 800;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 0.06em;
}
.sidebar-box-price {
    font-size: 22px;
    font-weight: 800;
    color: var(--primary);
}
.sidebar-slots {
    display: flex;
    flex-direction: column;
    gap: 0;
}
.sidebar-slot {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1.5px solid var(--nonagenary);
    transition: background 0.2s;
}
.sidebar-slot:last-child { border-bottom: none; }
.sidebar-slot.filled { background: var(--octagenary); }
.sidebar-slot.active { background: rgba(232, 118, 43, 0.04); }
.sidebar-slot-img {
    width: 52px;
    height: 42px;
    object-fit: contain;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,.12));
    flex-shrink: 0;
}
.sidebar-slot-info { flex: 1; }
.sidebar-slot-name {
    display: block;
    font-size: 13px;
    font-weight: 800;
    color: var(--text3);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.sidebar-slot-qty { font-size: 11px; color: var(--text11); }
.sidebar-slot-variant {
    font-size: 10px;
    font-weight: 700;
    border-radius: 20px;
    padding: 2px 8px;
    text-transform: uppercase;
    flex-shrink: 0;
}
.sidebar-slot-variant.normal { background: rgba(232,118,43,.12); color: var(--primary); }
.sidebar-slot-variant.spicy  { background: rgba(220,60,30,.10); color: #dc3c1e; }
.sidebar-slot-empty-num {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--nonagenary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 800;
    color: var(--text15);
    flex-shrink: 0;
}
.sidebar-slot-empty-lbl { font-size: 12px; color: var(--text15); }
.sidebar-slot.active .sidebar-slot-empty-num { background: rgba(232,118,43,.15); color: var(--primary); }
.sidebar-slot.active .sidebar-slot-empty-lbl { color: var(--primary); }
.sidebar-includes {
    padding: 10px 16px;
    font-size: 11px;
    color: var(--text11);
    background: var(--octagenary);
    border-top: 1.5px solid var(--nonagenary);
}
.sidebar-includes strong { font-weight: 700; color: var(--text9); }

/* ========== RESPONSIVE ========== */
@media (max-width: 400px) {
    .banner-img { height: 70px; }
    .title-line { font-size: 36px; }
    .prod-img { max-height: 54px; }
    .prod-img-wrap { height: 56px; }
}
</style>
